import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #touchStartY = 0;
  #touchStartX = 0;
  #isVerticalSwipe = false;
  #isHorizontalSwipe = false;
  #isNavigating = false;
  #targetIndex = null;
  #lastScrollLeft = 0;
  #lastWheelTime = 0;
  #lastTouchTime = 0;
  #GESTURE_COOLDOWN = 1000;
  #isProcessingWheel = false;
  #isProcessingTouch = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('wheel', this.handleWheel, { passive: false });
    this.refs.dialog.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.refs.dialog.addEventListener('touchend', this.handleTouchEnd, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('wheel', this.handleWheel);
    this.refs.dialog.removeEventListener('touchstart', this.handleTouchStart);
    this.refs.dialog.removeEventListener('touchmove', this.handleTouchMove);
    this.refs.dialog.removeEventListener('touchend', this.handleTouchEnd);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    // CRITICAL: Block during programmatic navigation to prevent state corruption
    if (this.#isNavigating) return;

    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Handles mouse wheel events for scrolling navigation.
   * @param {WheelEvent} event - The wheel event.
   */
  handleWheel = (event) => {
    // Always prevent default to stop native scroll
    event.preventDefault();
    event.stopPropagation();

    // ATOMIC GATE: If already processing a wheel event, block immediately
    if (this.#isProcessingWheel) {
      return;
    }

    const now = Date.now();

    // THROTTLE: Ignore rapid-fire events from same gesture
    if (now - this.#lastWheelTime < this.#GESTURE_COOLDOWN) {
      return;
    }

    // Set processing flag FIRST and keep it true for entire cooldown duration
    this.#isProcessingWheel = true;

    // Set timestamp to block subsequent gestures
    this.#lastWheelTime = now;

    // Clear processing flag after cooldown period expires
    setTimeout(() => {
      this.#isProcessingWheel = false;
    }, this.#GESTURE_COOLDOWN);

    // Block all scrolling if navigation is in progress
    if (this.#isNavigating) {
      return;
    }

    const isVerticalScroll = Math.abs(event.deltaY) > Math.abs(event.deltaX);
    const isHorizontalScroll = Math.abs(event.deltaX) > Math.abs(event.deltaY);

    // Only handle significant scroll (not both at once)
    if (!isVerticalScroll && !isHorizontalScroll) {
      return;
    }

    // Set lock
    this.#isNavigating = true;

    const { media, thumbnails } = this.refs;
    if (!thumbnails || !media.length) {
      this.#isNavigating = false;
      return;
    }

    // Use targetIndex if navigation is in progress, otherwise use aria-selected
    const activeButton = thumbnails.querySelector('button[aria-selected="true"]');
    const currentIndex = this.#targetIndex !== null
      ? this.#targetIndex
      : (activeButton ? Array.from(thumbnails.children).indexOf(activeButton) : 0);

    let newIndex = currentIndex;

    if (isVerticalScroll) {
      // Vertical: up = previous, down = next
      if (event.deltaY < 0) {
        newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      } else {
        newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : media.length - 1;
      }
    } else if (isHorizontalScroll) {
      // Horizontal: left = previous, right = next
      if (event.deltaX < 0) {
        newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      } else {
        newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : media.length - 1;
      }
    }

    if (newIndex !== currentIndex) {
      const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
      this.selectThumbnail(newIndex, { behavior });
    } else {
      // No change, unlock immediately
      this.#isNavigating = false;
    }
  };

  /**
   * Handles touch start events for swipe detection.
   * @param {TouchEvent} event - The touch event.
   */
  handleTouchStart = (event) => {
    this.#touchStartY = event.touches[0].clientY;
    this.#touchStartX = event.touches[0].clientX;
    this.#isVerticalSwipe = false;
    this.#isHorizontalSwipe = false;
  };

  /**
   * Handles touch move events to detect swipes.
   * @param {TouchEvent} event - The touch event.
   */
  handleTouchMove = (event) => {
    // Block all touch movement if navigation is in progress
    if (this.#isNavigating) {
      event.preventDefault();
      return;
    }

    if (!this.#touchStartY || !this.#touchStartX) return;

    const deltaY = Math.abs(event.touches[0].clientY - this.#touchStartY);
    const deltaX = Math.abs(event.touches[0].clientX - this.#touchStartX);

    // Detect if this is a vertical swipe (more vertical than horizontal)
    if (deltaY > deltaX && deltaY > 30) {
      this.#isVerticalSwipe = true;
      this.#isHorizontalSwipe = false;
      event.preventDefault();
    }
    // Detect if this is a horizontal swipe (more horizontal than vertical)
    else if (deltaX > deltaY && deltaX > 30) {
      this.#isHorizontalSwipe = true;
      this.#isVerticalSwipe = false;
      event.preventDefault();
    }
  };

  /**
   * Handles touch end events to navigate based on swipes.
   * @param {TouchEvent} event - The touch event.
   */
  handleTouchEnd = (event) => {
    if ((!this.#isVerticalSwipe && !this.#isHorizontalSwipe) || (!this.#touchStartY && !this.#touchStartX)) {
      this.#touchStartY = 0;
      this.#touchStartX = 0;
      this.#isVerticalSwipe = false;
      this.#isHorizontalSwipe = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // ATOMIC GATE: If already processing a touch event, block immediately
    if (this.#isProcessingTouch) {
      this.#touchStartY = 0;
      this.#touchStartX = 0;
      this.#isVerticalSwipe = false;
      this.#isHorizontalSwipe = false;
      return;
    }

    const now = Date.now();

    // THROTTLE: Ignore rapid-fire swipes from same gesture
    if (now - this.#lastTouchTime < this.#GESTURE_COOLDOWN) {
      this.#touchStartY = 0;
      this.#touchStartX = 0;
      this.#isVerticalSwipe = false;
      this.#isHorizontalSwipe = false;
      return;
    }

    // Set processing flag FIRST and keep it true for entire cooldown duration
    this.#isProcessingTouch = true;

    // Set timestamp to block subsequent gestures
    this.#lastTouchTime = now;

    // Clear processing flag after cooldown period expires
    setTimeout(() => {
      this.#isProcessingTouch = false;
    }, this.#GESTURE_COOLDOWN);

    // Block if navigation is in progress
    if (this.#isNavigating) {
      this.#touchStartY = 0;
      this.#touchStartX = 0;
      this.#isVerticalSwipe = false;
      this.#isHorizontalSwipe = false;
      return;
    }

    // Set lock
    this.#isNavigating = true;

    const { media, thumbnails } = this.refs;
    if (!thumbnails || !media.length) {
      this.#isNavigating = false;
      this.#touchStartY = 0;
      this.#touchStartX = 0;
      this.#isVerticalSwipe = false;
      this.#isHorizontalSwipe = false;
      return;
    }

    // Use targetIndex if navigation is in progress, otherwise use aria-selected
    const activeButton = thumbnails.querySelector('button[aria-selected="true"]');
    const currentIndex = this.#targetIndex !== null
      ? this.#targetIndex
      : (activeButton ? Array.from(thumbnails.children).indexOf(activeButton) : 0);

    let newIndex = currentIndex;

    if (this.#isVerticalSwipe) {
      const touchEndY = event.changedTouches[0].clientY;
      const deltaY = this.#touchStartY - touchEndY;

      if (deltaY > 50) {
        // Swipe up = next (stop at last)
        newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : media.length - 1;
      } else if (deltaY < -50) {
        // Swipe down = previous (stop at first)
        newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      }
    } else if (this.#isHorizontalSwipe) {
      const touchEndX = event.changedTouches[0].clientX;
      const deltaX = this.#touchStartX - touchEndX;

      if (deltaX > 50) {
        // Swipe left = next (stop at last)
        newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : media.length - 1;
      } else if (deltaX < -50) {
        // Swipe right = previous (stop at first)
        newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      }
    }

    if (newIndex !== currentIndex) {
      const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
      this.selectThumbnail(newIndex, { behavior });
    } else {
      // No change, unlock immediately
      this.#isNavigating = false;
    }

    this.#touchStartY = 0;
    this.#touchStartX = 0;
    this.#isVerticalSwipe = false;
    this.#isHorizontalSwipe = false;
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Handles keyboard navigation in the zoom dialog.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    // Arrow key navigation
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();

      // Block if navigation is in progress
      if (this.#isNavigating) return;

      const { media, thumbnails } = this.refs;
      if (!thumbnails || !media.length) return;

      // Use targetIndex if navigation is in progress, otherwise use aria-selected
      const activeButton = thumbnails.querySelector('button[aria-selected="true"]');
      const currentIndex = this.#targetIndex !== null
        ? this.#targetIndex
        : (activeButton ? Array.from(thumbnails.children).indexOf(activeButton) : 0);

      let newIndex = currentIndex;

      // Left/Up = previous (stop at first), Right/Down = next (stop at last)
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : 0;
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : media.length - 1;
      }

      if (newIndex !== currentIndex) {
        // Set lock IMMEDIATELY before calling selectThumbnail
        this.#isNavigating = true;
        const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
        this.selectThumbnail(newIndex, { behavior });
      }
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    // Block if navigation is in progress
    if (this.#isNavigating) return;

    // Set lock IMMEDIATELY before calling selectThumbnail
    this.#isNavigating = true;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    // Set target index immediately to track where we're navigating to
    this.#targetIndex = index;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));

    // Clear navigating flag and target index after transition completes
    const transitionDuration = options.behavior === 'smooth' ? 600 : 100;
    setTimeout(() => {
      this.#isNavigating = false;
      this.#targetIndex = null;
    }, transitionDuration);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
